import { createBrowserRouter, Navigate } from 'react-router-dom';
import React, { FC } from 'react';
import { HTML5Backend } from 'react-dnd-html5-backend';
import { DndProvider } from 'react-dnd';
import ProtectedRoute from './components/protectedRoute/protectedRoute';
import Sidebar from './components/sidebar/sidebar';
import Users from './pages/administration/users/users';
import CustomFields from './pages/settings/customFields/customFields';
import OrganizationSettings from './pages/administration/organizationSettings/organizationSettings';
import Ads from './pages/adsManagement/ads/ads';
import AdGroups from './pages/adsManagement/adGroups/adGroups';
import Campaigns from './pages/adsManagement/campaigns/campaigns';
import Auth from './pages/auth/auth';
import TrackingSources from './pages/adsManagement/trackingSources/trackingSources';
import HubspotRoute from './components/protectedRoute/integrationRoutes/hubspotRoute';
import MicrosoftRoute from './components/protectedRoute/integrationRoutes/microsoftRoute';
import AdPage from './pages/adPage/AdPage';
import DeveloperConsole from './pages/settings/developerConsole/DeveloperConsole';
import ResetPasswordDialog from './dialogs/resetPasswordDialog';
import Crm from './pages/integrations/crm/crm/crm';
import AdProviders from './pages/integrations/adProviders/adProviders';
import HubSpotPanel from './pages/settings/integrations/hubSpotPanel';
import GoogleTabPanel from './pages/settings/integrations/googleTabPanel';
import BingPanel from './pages/settings/integrations/bingPanel';
import DealSettings from './pages/integrations/crm/dealSettings/dealSettings';
import HubspotFormsTable from './components/tables/hubspotFormsTable';
import CalculationsPanel
  from './pages/administration/organizationSettings/organizationSettingsPanels/calculationsPanel';
import ReportingPanel from './pages/administration/organizationSettings/organizationSettingsPanels/reportingPanel';
import PageViewsPanel from './pages/administration/organizationSettings/organizationSettingsPanels/pageViewsPanel';
import OrganizationTypePanel from './pages/administration/organizationSettings/organizationSettingsPanels/organizationTypePanel';
import ContactSettings from './pages/integrations/crm/contactSettings/contactSettings';
import Error from './components/error/error';
import ProfilePage from './pages/profile/profilePage';
import ResetPassword from './components/resetPassword/resetPassword';
import TwoFactorAuthentication from './components/twoFactorAuthentication/twoFactorAuthentication';
import MyProfile from './components/myProfile/myProfile';
import FacebookRoute from './components/protectedRoute/integrationRoutes/facebookRoute';
import FacebookPanel from './pages/settings/integrations/facebookPanel';
import ConversionPage from './pages/conversionPage/conversionPage';
import OtherConversion from './components/otherConversion/otherConversion';
import GoalOptimization from './components/goalOptimization/GoalOptimization';
import ConversionName from './components/conversionName/conversionName';
import ConversionValue from './components/conversionValue/ConversionValue';
import ConfiguredOtherConversion from './components/configuredOtherConversion/configuredOtherConversion';
import ConversionSubmitTypePage from './pages/submitType/conversionSubmitTypePage';
import Welcome from './pages/welcome/welcome';
import Subscription from './pages/settings/subscription';
import ImpersonateRoute from './pages/admin/impersonateRoute/impersonateRoute';
import GoogleConversionsPage from './pages/googleConversions/googleConversions';
import TrackingCodePage from './pages/trackingCodePage/trackingCodePage';
import PaymentProvider from './pages/integrations/paymentProvider/paymentProvider';
import PaddleTabPanel from './pages/integrations/paymentProvider/paddleTabPanel';
import ForgotPasswordDialog from './dialogs/forgotPasswordDialog';
import ManagerialAccountPanel
  from './pages/administration/organizationSettings/organizationSettingsPanels/managerialAccountPanel';
import ShopifyRoute from './components/protectedRoute/integrationRoutes/shopifyRoute';
import EcommerceTab from './pages/integrations/eCommerce/ecommerceTab';
import ConnectToShopify from './pages/integrations/eCommerce/connectToShopify';
import NewDashboard from './pages/newDashboard/NewDashboard';
import FormSubmission from './pages/FormSubmissions/FormSubmission';
import SalesforcePanel from './pages/settings/integrations/salesforcePanel';
import MondayPanel from './pages/settings/integrations/mondayPanel';
import SalesforceRoute from './components/protectedRoute/integrationRoutes/salesforceRoute';
import LinkedInPanel from './pages/settings/integrations/linkedInPanel';
import ExpiredTrial from './pages/expiredTrial/expiredTrial';
import ImportAds from './pages/importAds/ImportAds';
import TiktokPanel from './pages/settings/integrations/tiktokPanel';
import TiktokRoute from './components/protectedRoute/integrationRoutes/tiktokRoute';
import ShopifyWelcome from './pages/welcome/shopifyWelcome';
import EmailProviders from './pages/integrations/emailProviders/emailProviders';
import MailchimpPanel from './pages/settings/integrations/mailchimpPanel';
import MailchimpRoute from './components/protectedRoute/integrationRoutes/mailchimpRoute';
import ShopifyPage from './pages/shopifyPage/ShopifyPage';
import AnalyticsPage from './pages/integrations/analytics/analytics';
import AnalyticsPanel from './pages/settings/integrations/analyticsPanel';
import SnapchatPanel from './pages/settings/integrations/snapchatPanel';
import SnapchatRoute from './components/protectedRoute/integrationRoutes/snapchatRoute';
import ShopifyPayPage from './pages/shopifyPayPage/ShopifyPayPage';
import ShopifyPayRoute from './components/protectedRoute/integrationRoutes/shopifyPayRoute';
import Setup from './pages/setup/setup';
import TokensPage from './pages/developers/tokens/tokens';
import TokenSessionsTab from './pages/developers/tokens/tokensSessions';
import DeveloperTokensTab from './pages/developers/tokens/tokensDeveloper';
import OAuthConnect from './pages/oauth/oauthConnect';
import OAuthConsent from './pages/oauth/oauthConsent';

const newLocation = localStorage.getItem('landingPage') ? localStorage.getItem('landingPage') : 'dashboard';

const Redirect: FC<{to: string}> = (props) => {
  window.location.replace(props.to);
  return null;
};

const router = createBrowserRouter([
  {
    path: '/',
    element: (
      <ProtectedRoute>
        <Sidebar />
      </ProtectedRoute>
    ),
    errorElement: <Error />,
    children: [
      {
        path: 'dashboard',
        index: true,
        element: <NewDashboard />,
      },
      {
        path: 'shopify/onboarding',
        element: <ShopifyPage />,
      },
      {
        path: 'profile-page',
        element: <ProfilePage />,
        children: [
          {
            path: 'profile',
            element: <MyProfile />,
          }, {
            path: 'password',
            element: <ResetPassword />,
          }, {
            path: 'authentication',
            element: <TwoFactorAuthentication />,
          },
        ],
      },
      {
        path: 'ad-page/:id',
        element: <AdPage />,
      },
      {
        path: 'administration',
        children: [
          {
            path: 'users',
            element: <Users />,
          },
        ],
      },
      {
        path: 'administration',
        children: [
          {
            path: 'users',
            element: <Users />,
          },
        ],
      },
      {
        path: 'integrations',
        children: [{
          path: 'crm',
          element: <Crm />,
          children: [
            {
              path: 'hubspot',
              element: <HubSpotPanel />,
              children: [
                {
                  index: true,
                  element: <HubspotFormsTable />,
                },
                {
                  path: 'forms',
                  element: <HubspotFormsTable />,
                }, {
                  path: 'contact-settings',
                  element: <ContactSettings />,

                }, {
                  path: 'deal-settings',
                  element: <DndProvider backend={HTML5Backend}><DealSettings /></DndProvider>,
                }],
            },
            {
              path: 'salesforce',
              element: <SalesforcePanel />,
            },
            {
              path: 'monday',
              element: <MondayPanel />,
            },
          ],
        },
        {
          path: 'ad-providers',
          element: <AdProviders />,
          children: [
            {
              path: 'google',
              element: <GoogleTabPanel />,
            },
            {
              path: 'facebook',
              element: <FacebookPanel />,
            },
            {
              path: 'bing',
              element: <BingPanel />,
            },
            {
              path: 'linked-in',
              element: <LinkedInPanel />,
            },
            {
              path: 'tik-tok',
              element: <TiktokPanel />,
            },
            {
              path: 'snapchat',
              element: <SnapchatPanel />,
            },
          ],
        },
        {
          path: 'email-providers',
          element: <EmailProviders />,
          children: [
            {
              path: 'mailchimp',
              element: <MailchimpPanel />,
            },

          ],
        },
        {
          path: 'analytics',
          element: <AnalyticsPage />,
          children: [
            {
              path: 'google-analytics',
              element: <AnalyticsPanel />,
            },

          ],
        },
        {
          path: 'payment-provider',
          element: <PaymentProvider />,
          children: [
            {
              path: 'paddle',
              element: <PaddleTabPanel />,
            },
          ],
        },
        {
          path: 'e-commerce',
          element: <EcommerceTab />,
          children: [
            {
              path: 'shopify',
              element: <ConnectToShopify />,
            },
          ],
        },
        ],
      }, {
        path: 'settings',
        children: [
          {
            path: 'custom-fields',
            element: <CustomFields />,
          },
          {
            path: 'organization-settings',
            element: <OrganizationSettings />,
            children: [
              {
                path: 'managerialAccount',
                element: <ManagerialAccountPanel />,
              },
              {
                path: 'organization-type',
                element: <OrganizationTypePanel />,
              },
              {
                path: 'calculations',
                element: <CalculationsPanel />,
              }, {
                path: 'reporting',
                element: <ReportingPanel />,
              }, {
                path: 'page-views',
                element: <PageViewsPanel />,
              },
            ],
          },
          {
            path: 'developer-console',
            element: <DeveloperConsole />,
          }, {
            path: 'subscription',
            element: <Subscription />,
          },
        ],
      }, {
        path: 'ad-management',
        children: [
          {
            path: 'ads',
            element: <Ads />,
          },
          {
            path: 'ad-groups',
            element: <AdGroups />,
          }, {
            path: 'campaigns',
            element: <Campaigns />,
          },
          {
            path: 'tracking-sources',
            element: <TrackingSources />,
          },
          {
            path: 'import-ads',
            element: <ImportAds />,
          },
        ],
      },
      {
        path: 'developers',
        children: [{
          path: 'console',
          element: <DeveloperConsole />,
        }, {
          path: 'tokens',
          element: <TokensPage />,
          children: [
            {
              index: true,
            element: <Navigate to="/developers/tokens/sessions" replace />,
            },
            {
            path: 'sessions',
            element: <TokenSessionsTab />,
            },
            {
            path: 'developer',
            element: <DeveloperTokensTab />,
          },
          {
            path: 'all',
            element: <Navigate to="/developers/tokens/sessions" replace />,
          },
          {
            path: 'revoked',
            element: <Navigate to="/developers/tokens/sessions" replace />,
            },
          ],
        }, {
          path: 'api-docs',
          element: <Redirect to="https://digtective.docs.apiary.io/" />,
        },
        ],
      },
      {
        path: 'setup',
        element: <Setup />,
      },
      {
        path: 'form-submission',
        index: true,
        element: <FormSubmission />,
      },
      {
        path: '/tracking/tracking-code',
        element: <TrackingCodePage />,
      },
      {
        path: '/tracking/google-conversions',
        element: <GoogleConversionsPage />,
      },
      {
        path: '/tracking/google-conversions/conversions',
        element: <ConversionPage />,
      },
      {
        path: '/tracking/google-conversions/customise-other',
        element: <ConversionSubmitTypePage />,
      },
      {
        path: '/tracking/google-conversions/other-conversions',
        element: <OtherConversion />,
        children: [
          {
            index: true,
            element: <Navigate to="/tracking/google-conversions/other-conversions/goals" replace />,
          },
          {
            path: 'goals',
            element: <GoalOptimization />,
          }, {
            path: 'conversion-name',
            element: <ConversionName />,

          }, {
            path: 'value',
            element: <ConversionValue />,
          },
          {
            path: 'configured',
            element: <ConfiguredOtherConversion />,
          },
        ],
      },
    ],
  },
  {
    path: '/auth',
    element: (
      <ProtectedRoute>
        <Auth />
      </ProtectedRoute>
    ),
    children: [{
      path: 'reset-password',
      element: <ResetPasswordDialog />,
    },
    {
      path: 'forgot-password',
      element: <ForgotPasswordDialog />,
    },
    ],
    errorElement: <Error />,
  },
  {
    path: '/expiredTrial',
    element: <ExpiredTrial />,
  },
  {
    path: '/welcome',
    element: <Welcome />,
    errorElement: <Error />,
  },
  {
    path: '/shopify/welcome',
    element: <ShopifyWelcome />,
    errorElement: <Error />,
  },
  {
    path: '/facebook',
    element: <FacebookRoute />,
  },
  {
    path: '/hubspot',
    element: <HubspotRoute />,
  },
  {
    path: '/tiktok',
    element: <TiktokRoute />,
  },
  {
    path: '/mailchimp',
    element: <MailchimpRoute />,
  },
  {
    path: '/salesforce',
    element: <SalesforceRoute />,
  },
  {
    path: '/snapchat',
    element: <SnapchatRoute />,
  },
  {
    path: '/microsoft',
    element: <MicrosoftRoute />,
  },
  {
    path: '/shopify',
    element: <ShopifyRoute />,
  },
  {
    path: '/shopify/paid',
    element: <ShopifyPayRoute />,
  },
  {
    path: '/shopify/locked',
    element: <ShopifyPayPage />,
  },
  {
    path: '/admin/user',
    element: <ImpersonateRoute />,
  },
  {
    path: '/oauth-connect',
    element: <OAuthConnect />,
    errorElement: <Error />,
  },
  {
    path: '/oauth-consent',
    element: <OAuthConsent />,
    errorElement: <Error />,
  },
  {
    path: '*',
    element: <Navigate to={`/${newLocation}`} />,
    errorElement: <Error />,
  },
]);

export default router;
